<template>
    <v-form ref="form">
        <fe-spinner v-if="!loaded" />
        <periods-expectations
            v-if="!formError"
            v-model="localValue"
            :strategy="strategy"
            @loaded="loaded = true"
        />
        <div v-else>
            {{formError}}
        </div>
        <div class="mt-5 d-flex card-bottom">
            <fe-btn usage="secondary" @click="$emit('go','back')" data-test="create-intervention-point-sheet-back">Back</fe-btn>
            <fe-btn @click="nextStep" data-test="create-intervention-point-sheet-step-add">Continue</fe-btn>
        </div>
    </v-form>
</template>

<script>
    import PeriodsExpectations from '@/components/modules/intervention/common/PeriodsExpectations'

    export default {
        name: 'MonitorPointSheet',
        components: { PeriodsExpectations },
        props: {
            value: {
                type: Object,
                default: () => {
                    return {
                        monitors: [],
                    }
                }
            },
            strategy: {
                type: Object,
                defualt: null,
            }
        },
        computed: {
            formError() {
                if (!this.strategy?.id) {
                    return "Please go back and select a strategy";
                }

            },
        },
        watch: {
            localValue(v) {
                this.$emit('input', v)
            }
        },
        data() {
            return {
                loaded: false,
                localValue: {monitors: []},
            }
        },
        created() {
            if (this.value) { // copy over if point sheet
                this.localValue = this.$_.assign(this.localValue, this.value)
            }
        },
        methods: {
            nextStep() {
                if (this.$refs.form.validate()) {
                    this.$emit('go')
                }
            },
            changeScore(v) {
                this.formatData()
                this.validatePeriods(this.selectedPeriods)
            },
            formatData() {
                let monitors = []
                this.selectedPeriods.forEach(p => {
                    p.expectations?.forEach(e => {
                        monitors.push({
                            sub_category_id: e.sub_category_id,
                            expectation_name: e.expectation_name,
                            lowest_score: e.lowest_score,
                            highest_score: e.highest_score,
                            monitor_period_id: p.monitor_period_id,
                            force_score_range: e.force_score_range,
                            no_score_flag: e.no_score_flag ? true : false,
                        })
                    })
                })
                this.localValue = {monitors}
            },
            resetExpectations(v) {
                if (v.length===0) this.selectedExpectations = this.expectations.filter(r => r.require_flag)
                this.$emit('expectation-change', v)
                this.$emit('selectedExpectations', v)
                this.setTableData()
                this.formatData()
            },
            resetPeriods(v) {
                if (v.length===0) this.selectedPeriods = this.periods.filter(r => r.require_flag)
                this.$emit('period-change', v)
                this.setTableData()
                this.formatData()
            },
            setTableData() {
                if (!this.loaded) {
                    return
                }
                const previousValues = new Map()
                this.localValue.monitors?.forEach(mon => {
                    previousValues.set(mon.sub_category_id + '-' + mon.monitor_period_id, mon)
                })
                this.selectedPeriods.forEach(p => {
                    p.expectations = []
                    this.selectedExpectations.forEach(e => {
                        const previous = previousValues.get(e.sub_category_id + '-' + p.monitor_period_id) || {}
                        const mon = {
                            sub_category_id: e.sub_category_id,
                            no_score_flag: false,
                            force_score_range: e.force_score_range,
                            expectation_name: e.expectation_name,
                            lowest_score: e.lowest_score,
                            highest_score: e.highest_score
                        }
                        if (!mon.force_score_range && previous.hasOwnProperty('lowest_score')) {
                            mon.lowest_score = previous.lowest_score
                        }
                        if (!mon.force_score_range && previous.hasOwnProperty('highest_score')) {
                            mon.highest_score = previous.highest_score
                        }
                        if (previous.no_score_flag) {
                            mon.no_score_flag = previous.no_score_flag
                        }
                        p.expectations.push(mon)
                    })
                })
            },
            loadData() {
                if (!this.strategy?.id) {
                    return
                }
                Promise.all([
                    this.$modelGet('interventionTypeMonitor', { intervention_type_id: this.strategy.id }).then(expectations => {
                        this.expectations = expectations
                        this.expectations.forEach(r => { if (r.require_flag) r.disabled = true })
                    }),
                    this.$modelGet('interventionTypePeriod', { intervention_type_id: this.strategy.id }).then(periods => {
                        this.periods = periods.filter(x => x.active)
                        this.periods.forEach(r => { if (r.require_flag) r.disabled = true })
                    })
                ]).then(() => {
                    if (!this.localValue.monitors) {
                        this.selectedExpectations = this.expectations.filter(r => r.require_flag)
                        this.selectedPeriods = this.periods.filter(r => r.require_flag)
                    } else {
                        const selectedExpectionIdSet = new Set(this.localValue.monitors.map(mon => mon.sub_category_id))
                        const expectationMap = new Map(this.expectations.map(e => [e.sub_category_id, e]))
                        this.selectedExpectations = [
                            // sets are ordered, so return in the order the user selected (with duplicates removed)
                            ...selectedExpectionIdSet.intersection(expectationMap).values().map(id => expectationMap.get(id)),
                            // add any required expectations the user didn't already have
                            ...this.expectations.filter(e => e.require_flag && !selectedExpectionIdSet.has(e.sub_category_id))
                        ]

                        const selectedPeriodIdSet = new Set(this.localValue.monitors.map(mon => mon.monitor_period_id))
                        const periodMap = new Map(this.periods.map(p => [p.monitor_period_id, p]))
                        this.selectedPeriods = [
                            // sets are ordered, so return in the order the user selected (with duplicates removed)
                            ...selectedPeriodIdSet.intersection(periodMap).values().map(id => periodMap.get(id)),
                            // add any required periods the user didn't already have
                            ...this.periods.filter(p => p.require_flag && !selectedPeriodIdSet.has(p.monitor_period_id))
                        ]
                    }
                    this.loaded = true

                    this.setTableData()
                })
            },
        }
    }
</script>

<style lang="scss" scoped>

</style>
